import { Grid } from '@mui/material';
import {
  HSpacer,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';

import { renderInputText } from '../../../../components/right-panel-info/Common';
import { InfoContainer } from '../../../../components/right-panel-info/RightPanel.styled';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { Project } from '../../../../domain/project/Project';
import { useUpdateProjectGeoFence } from '../../../../hooks/project/useUpdateProjectGeoFence';
import { useSearchVehicles } from '../../../../hooks/vehicle/useSearchVehicles';
import { useUpdateVehicle } from '../../../../hooks/vehicle/useUpdateVehicle';
import Text from '../../../../react-components/Text';
import { VehicleAvatar } from '../../../shared/VehicleAvatar';

interface PlannerGeoFenceBodyProps {
  geoFence: GeoFence;
  activeProject: Project;
}

export const PlannerGeoFenceBody = ({ geoFence, activeProject }: PlannerGeoFenceBodyProps) => {
  const { run: updateGeoFence } = useUpdateProjectGeoFence();
  const { run: updateVehicle } = useUpdateVehicle();

  const { data: paginatedVehicles } = useSearchVehicles({
    offset: 0,
    limit: 500,
  });

  const handleUpdate = (label: string, val: any) => {
    geoFence.update({ [label]: val });
    updateGeoFence({ geoFence });
  };

  const projectVehicles = paginatedVehicles?.docs ?? [];

  const assignedVehicles = projectVehicles.filter((vehicle) => vehicle.default_geo_fences?.includes(geoFence.id));

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ height: `100%`, backgroundColor: RoutalPalette.neutral00 }}
    >
      <InfoContainer>
        {renderInputText({
          labelIntlId: `planner.zones.geo_fence_name`,
          placeholderIntlId: `clients.data.details.label`,
          variant: `outlined`,
          property: `label`,
          size: `title`,
          handleUpdate,
          data: geoFence,
        })}

        <VSpacer medium />

        <Text intlId="planner.zones.linked_vehicles" variant="label" style={{ marginBottom: `8px` }} />

        <RoutalSelect
          id="filter-driver-geo_fences"
          popoverProps={{
            maxHeight: `unset`,
            options: projectVehicles.map((vehicle): RoutalSelectMultiSelectOption => {
              return {
                key: vehicle.id,
                checked: vehicle.default_geo_fences?.includes(geoFence.id),
                hideCheckIcon: false,
                closeOnClick: false,
                onClick: () => {
                  if (vehicle.default_geo_fences?.includes(geoFence.id)) {
                    updateVehicle({
                      vehicle,
                      changes: { default_geo_fences: vehicle.default_geo_fences.filter((geo) => geo !== geoFence.id) },
                    });
                  } else {
                    updateVehicle({
                      vehicle,
                      changes: { default_geo_fences: [...(vehicle.default_geo_fences ?? []), geoFence.id] },
                    });
                  }
                },
                content: (
                  <Grid container direction="row" alignItems="center">
                    <Grid item>
                      <VehicleAvatar vehicleId={vehicle.id} vehicleColor={vehicle.color?.value} />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      {vehicle.label && vehicle.label !== `` ? (
                        <RoutalTooltip title={vehicle.label} key="label">
                          <Text variant="body">
                            {vehicle.label.length > 40 ? `${vehicle.label.substring(0, 40)}...` : `${vehicle.label}`}
                          </Text>
                        </RoutalTooltip>
                      ) : (
                        <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                      )}
                    </Grid>
                  </Grid>
                ),
              };
            }),
          }}
        >
          <Grid container direction="row">
            {assignedVehicles.length === 0 ? (
              <Text variant="label" style={{ cursor: `pointer` }} intlId="planner.zones.no_assigned_vehicles" />
            ) : (
              <Text
                style={{ cursor: `pointer` }}
                intlId="planner.zones.assigned_vehicles"
                intlValues={{ num: assignedVehicles.length }}
              />
            )}
          </Grid>
        </RoutalSelect>
        <VSpacer medium />
      </InfoContainer>
    </Grid>
  );
};
